import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { useMemo } from 'react'
import { match } from 'ts-pattern'
import { z } from 'zod'
import { POLL_INTERVAL_MS, startCrawlNewUrls, useCrawlTaskStore } from '~/features/bots/stores/useCrawlerStore'
import { getQueryKey, useSuspenseQuery } from '~/services'
import { getApiClient, getStudioClient } from '~/stores'
import { showErrorToast } from '../../../../../../packages/ui-kit-next/src/components/ui/Toaster'
import { useDebounceEffect } from '../../../hooks/useDebounceEffect'
import { CrawlFrequency, RecurringSync, UpdateCrawlScheduleParams } from '../types'
import { useDsData } from './useKnowledgeSource'

export type UseWebSourceCrawlProps = {
  workspaceId: string
  botId: string
  kbId: string
  dsId: string
}

export type UseResyncProps = {
  workspaceId: string
  botId: string
  dsId: string
}

const IndexingTask = z.object({
  dsId: z.string(),
  kbId: z.string(),
  pageUrl: z.string(),
  urls: z.array(z.string()),
})

const calculateCronSchedule = (frequency: CrawlFrequency) => {
  const now = new Date()
  return match(frequency)
    .with('daily', () => `${now.getMinutes()} ${now.getHours()} * * *`)
    .with('weekly', () => `${now.getMinutes()} ${now.getHours()} * * ${now.getDay()}`)
    .with('monthly', () => `${now.getMinutes()} ${now.getHours()} ${now.getDate()} * *`)
    .with('never', () => undefined)
    .exhaustive()
}

export const useCrawlScheduleData = ({ workspaceId, botId, kbId, dsId }: UseWebSourceCrawlProps) => {
  const {
    dsData: { [dsId]: payload },
  } = useDsData({ workspaceId, botId, kbId })

  const {
    data: {
      state: { updatedAt: lastResync },
    },
  } = useSuspenseQuery('workspaces_/$workspaceId_/bots_/$botId_/states/$name', {
    workspaceId,
    botId,
    name: 'recurringSyncs',
    type: 'bot',
  })
  return { frequency: payload && payload.data.crawlFrequency ? payload.data.crawlFrequency : 'never', lastResync }
}

export const useResync = ({ workspaceId, botId }: { workspaceId: string; botId: string }) => {
  const crawlTaskStore = useCrawlTaskStore()
  const queryClient = useQueryClient()

  const {
    data: {
      state: { payload },
    },
  } = useSuspenseQuery('workspaces_/$workspaceId_/bots_/$botId_/states/$name', {
    workspaceId,
    botId,
    name: 'recurringSyncs',
    type: 'bot',
  })
  return useMutation({
    mutationFn: async ({ workspaceId, botId, dsId }: UseResyncProps) => {
      const apiClient = getApiClient({ workspaceId, botId })
      const websiteIndexingFile = await apiClient
        .listFiles({
          tags: { source: 'website-indexing' },
          sortField: 'createdAt',
          sortDirection: 'desc',
        })
        .then(({ files }) => files.find((file) => file.key.includes(dsId)))

      if (!websiteIndexingFile || !websiteIndexingFile.url) {
        return null
      }

      const content = IndexingTask.parse(await fetch(websiteIndexingFile.url).then((res) => res.json()))
      const crawlTask = await startCrawlNewUrls({ workspaceId, botId, ...content })
      await apiClient.setState({
        id: botId,
        type: 'bot',
        name: 'recurringSyncs',
        payload: { ...payload, updatedAt: DateTime.now().toISO() },
      })
      return crawlTask
    },
    onSuccess: async (eventId, { workspaceId, botId }) => {
      if (eventId) {
        crawlTaskStore.trackCrawlEvent({ eventId, botId, workspaceId })
      }
      await queryClient.invalidateQueries({
        queryKey: getQueryKey('workspaces_/$workspaceId_/bots_/$botId_/states/$name', {
          workspaceId,
          botId,
          name: 'recurringSyncs',
        }),
      })
    },
    onError: async () => {
      showErrorToast('Failed to resync the website source')
    },
  })
}

export const useUpdateCrawlSchedule = ({
  workspaceId,
  botId,
  kbId,
}: {
  workspaceId: string
  botId: string
  kbId: string
}) => {
  const queryClient = useQueryClient()
  const {
    data: {
      state: {
        payload: { recurringSyncs },
      },
    },
  } = useSuspenseQuery('workspaces_/$workspaceId_/bots_/$botId_/states/$name', {
    workspaceId,
    botId,
    name: 'recurringSyncs',
    type: 'bot',
  })
  const { data: dsData } = useSuspenseQuery('workspaces_/$workspaceId_/bots_/$botId_/states/$name', {
    workspaceId,
    botId,
    name: 'dsData',
    type: 'bot',
  })

  return useMutation({
    mutationFn: async ({ dsId, frequency }: UpdateCrawlScheduleParams) => {
      const cronSchedule = calculateCronSchedule(frequency)

      // Update the recurringSyncs state
      const newRecurringSyncs: RecurringSync = { ...(recurringSyncs || { daily: [], weekly: [], monthly: [] }) }

      for (const _frequency of Object.keys(newRecurringSyncs)) {
        newRecurringSyncs[_frequency as keyof RecurringSync] = newRecurringSyncs[
          _frequency as keyof RecurringSync
        ].filter((f) => f !== dsId)
      }
      if (frequency !== 'never') {
        newRecurringSyncs[frequency as keyof RecurringSync].push(dsId)
      }
      await getApiClient({ workspaceId, botId }).setState({
        id: botId,
        type: 'bot',
        name: 'recurringSyncs',
        payload: newRecurringSyncs,
      })

      // Update the dsData state
      const payload = { ...(dsData.state?.payload || {}) }
      if (!payload[kbId] || !payload[kbId][dsId]) {
        throw new Error(`DsData for ${dsId} not found. ${payload}`)
      }
      payload[kbId][dsId].data.crawlFrequency = frequency
      payload[kbId][dsId].data.cronSchedule = cronSchedule
      if (frequency === 'never') {
        delete payload[kbId][dsId].data.cronSchedule
        delete payload[kbId][dsId].data.crawlFrequency
      }
      await getApiClient({ workspaceId, botId }).setState({
        id: botId,
        type: 'bot',
        name: 'dsData',
        payload: { ...payload },
      })
      return await getStudioClient({ workspaceId, botId }).post('/publish')
    },
    onSuccess: async (_, { onSuccess: _onSuccess }) => {
      await queryClient.invalidateQueries({
        queryKey: getQueryKey('workspaces_/$workspaceId_/bots_/$botId_/states/$name', {
          workspaceId,
          botId,
          name: 'recurringSyncs',
        }),
      })
      await queryClient.invalidateQueries({
        queryKey: getQueryKey('workspaces_/$workspaceId_/bots_/$botId_/states/$name', {
          workspaceId,
          botId,
          name: 'dsData',
        }),
      })
      _onSuccess?.()
    },
  })
}

export const useWebSourceCrawl = ({ workspaceId, botId, kbId, dsId }: UseWebSourceCrawlProps) => {
  const crawlTaskStore = useCrawlTaskStore()
  const queryClient = useQueryClient()

  const crawlTask = useMemo(() => {
    return crawlTaskStore.tasks.find((task) => task.dsId === dsId)
  }, [crawlTaskStore.tasks, dsId])

  useDebounceEffect(
    () => {
      if (!crawlTask) return
      crawlTaskStore.cleanInactive()
      if (crawlTask.status !== 'pending') {
        queryClient.invalidateQueries({
          queryKey: getQueryKey('workspaces_/$workspaceId_/bots_/$botId_/knowledgeBases_/$kbId_/sources/$tags/all', {
            workspaceId,
            botId,
            kbId,
            tags: { dsId: dsId },
          }),
        })
      }
    },
    [botId, crawlTask, dsId, kbId, queryClient, workspaceId],
    POLL_INTERVAL_MS
  )

  return crawlTask
}
